import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { IconButton } from '@picsio/ui';
import { Settings } from '@picsio/ui/dist/icons';
import { useSelector } from 'react-redux';
import { Checkbox, ReactSelect } from '../../../UIComponents';
import DialogRadios from '../../dialogRadios';
import Logger from '../../../services/Logger';
import localization from '../../../shared/strings';
import { navigate } from '../../../helpers/history';
import { showDialog } from '../../dialog';
import sendEventToIntercom from '../../../services/IntercomEventService';
import UpgradePlan from '../../UpgradePlan';
import Tooltip from '../../Tooltip';
import isWatermarkSupportedFormat from '../helpers/isWatermarkSupportedFormat';

const WatermarkAssetsTab = (props) => {
  const {
    selectedAssets = [],
    selectedAssetsIds,
    isVisible,
    toggleVisibility,
    disabled,
    attach,
    allWatermarks,
    watermarkId,
    inProgress,
    canEditWatermarks,
  } = props;
  const isWatermarkingAllowed = useSelector((state) => state.user?.subscriptionFeatures?.watermarks);
  // Parent may pass only loaded assets while selectedAssetsIds is the full selection.
  // Do not disable from a partial unloaded view; allow remove when a watermark is already set.
  const isWatermarkFormatSupported = useMemo(() => {
    if (!selectedAssets.length) return true;
    if (selectedAssets.some((asset) => isWatermarkSupportedFormat(asset?.fileExtension))) {
      return true;
    }
    if ((selectedAssetsIds?.length || 0) > selectedAssets.length) return true;
    return false;
  }, [selectedAssets, selectedAssetsIds]);
  const isWatermarkToggleDisabled = !isWatermarkFormatSupported && !watermarkId;

  const options = allWatermarks.map(
    (watermark) => ({
      ...watermark, label: watermark.name, value: watermark._id,
    }),
  );

  const placeholder = options?.find(
    (item) => item._id === watermarkId,
  ) || options?.find((item) => item.isDefault);

  const [value, setValue] = useState(placeholder);
  const [checkbox, setCheckbox] = useState(watermarkId);
  const [overwrite, setOverwrite] = useState(true);

  const handleClick = () => {
    toggleVisibility('detailsAssetWatermarkVisibility');
  };

  useEffect(() => {
    setCheckbox(watermarkId);
    const watermarkOptions = allWatermarks.map((watermark) => ({
      ...watermark,
      label: watermark.name,
      value: watermark._id,
    }));
    setValue(
      watermarkOptions.find((item) => item._id === watermarkId)
        || watermarkOptions.find((item) => item.isDefault),
    );
  }, [watermarkId, allWatermarks]);

  const handleChangeValue = (event) => {
    const isItOld = watermarkId === event._id;
    if (!isItOld && watermarkId) {
      if (selectedAssetsIds?.length === 1) {
        attach(true, true, selectedAssetsIds, event && event._id);
        setValue(event);
      } else {
        new DialogRadios({
          title: localization.WATERMARKS.applyWatermark,
          text: 'Apply to',
          items: [
            {
              label: 'All assets',
              value: 'allAssets',
              checked: overwrite,
              description: '',
            },
            {
              label: 'Non-watermarked assets',
              value: 'nonWatermarkedAssets',
              checked: !overwrite,
              description: '',
            },
          ],
          description: localization.WATERMARKS.titleAttachWatermark(selectedAssetsIds?.length),
          cancelName: localization.DIALOGS.MOVE_ASSETS_DIALOG.CANCEL_TEXT,
          okName: localization.DIALOGS.MOVE_ASSETS_DIALOG.OK_TEXT,
          onOk: (value) => {
            if (value === 'allAssets') {
              attach(true, true, selectedAssetsIds, event && event._id);
            } else {
              attach(false, true, selectedAssetsIds, event && event._id);
            }
            setValue(event);
          },
        });
      }
      Logger.log('UI', 'WatermarkDialogShow');
    } else {
      setValue(event);
    }
  };

  const handleChangeCheckbox = () => {
    if (watermarkId) {
      if (selectedAssetsIds?.length === 1) {
        attach(true, true, selectedAssetsIds);
      } else {
        showDialog({
          title: localization.WATERMARKS.removeWatermark,
          text: localization.WATERMARKS.titleDetachWatermark(selectedAssetsIds.length),
          textBtnOk: localization.WATERMARKS.btnOkRemoveWatermark,
          onOk() {
            attach(true, true, selectedAssetsIds);
          },
          onCancel() {},
        });
      }
      setValue(placeholder);
    } else if (selectedAssetsIds?.length === 1) {
      attach(true, true, selectedAssetsIds, value && value._id);
    } else {
      new DialogRadios({
        title: localization.WATERMARKS.applyWatermark,
        text: 'Apply to',
        items: [
          {
            label: 'All assets',
            value: 'allAssets',
            checked: overwrite,
            description: '',
          },
          {
            label: 'Non-watermarked assets',
            value: 'nonWatermarkedAssets',
            checked: !overwrite,
            description: '',
          },
        ],
        description: localization.WATERMARKS.titleAttachWatermark(selectedAssetsIds?.length),
        cancelName: localization.DIALOGS.MOVE_ASSETS_DIALOG.CANCEL_TEXT,
        okName: localization.DIALOGS.MOVE_ASSETS_DIALOG.OK_TEXT,
        onOk: (val) => {
          if (val === 'allAssets') {
            attach(true, true, selectedAssetsIds, value && value._id);
          } else {
            attach(false, true, selectedAssetsIds, value && value._id);
          }
        },
      });
    }
    Logger.log('User', selectedAssetsIds?.length > 1 ? 'WatermarkMultipleAdded' : 'WatermarkSingleAdded', placeholder?.name);
    sendEventToIntercom('Watermark applied');
  };

  const openWatermarks = () => {
    navigate('/teammates?tab=watermarking');
  };

  const handleClickUpgradePlan = () => {
    Logger.log('User', 'WatermarksUpgradePlanClicked');
    navigate('/billing?tab=overview');
  };

  return (
    <div
      data-qa="details-component-watermarks"
      className={clsx('detailsPanel__item', { act: isVisible, disabled })}
    >
      <div className="detailsPanel__title">
        <span
          className={clsx('detailsPanel__title_text')}
          onClick={handleClick}
          role="presentation"
        >
          {localization.DETAILS.titleWatermarks}
          <If condition={!isWatermarkingAllowed}>
            <UpgradePlan onClick={handleClickUpgradePlan} tooltip={localization.UPGRADE_PLAN.tooltipPlanLimitations} />
          </If>
        </span>
        <div className="detailsPanel__title_actions">
          <If condition={canEditWatermarks && isWatermarkingAllowed}>
            <IconButton
              onClick={openWatermarks}
              size="lg"
              className="picsioWatermarks"
            >
              <Settings />
            </IconButton>
          </If>
          <If condition={!disabled && isWatermarkingAllowed}>
            <Tooltip
              content={isWatermarkToggleDisabled ? localization.WATERMARKS.tooltipUnsupportedFormat : null}
              placement="top"
            >
              <Checkbox
                value={checkbox}
                onChange={handleChangeCheckbox}
                slide
                inProgress={inProgress}
                disabled={isWatermarkToggleDisabled}
              />
            </Tooltip>
          </If>
        </div>
      </div>
      <CSSTransition in={isVisible} timeout={300} classNames="fade">
        <>
          <If condition={isVisible}>
            <div className={clsx('restrictSettings', { disabled })} style={{ position: 'relative', zIndex: '3' }}>
              <ReactSelect
                placeholder={placeholder && placeholder.name || 'Not selected'}
                disabled={disabled}
                options={options}
                isSearchable={false}
                value={value}
                onChange={(val) => handleChangeValue(val)}
              />
            </div>
          </If>
        </>
      </CSSTransition>
    </div>
  );
};

WatermarkAssetsTab.defaultProps = {
  selectedAssets: [],
  selectedAssetsIds: [],
  isVisible: false,
  disabled: false,
  allWatermarks: [],
  watermarkId: null,
};
WatermarkAssetsTab.propTypes = {
  selectedAssets: PropTypes.arrayOf(PropTypes.shape({
    fileExtension: PropTypes.string,
  })),
  selectedAssetsIds: PropTypes.arrayOf(PropTypes.string),
  isVisible: PropTypes.bool,
  toggleVisibility: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  attach: PropTypes.func.isRequired,
  allWatermarks: PropTypes.arrayOf(PropTypes.object),
  watermarkId: PropTypes.string,
  inProgress: PropTypes.bool.isRequired,
  canEditWatermarks: PropTypes.bool.isRequired,
};

export default WatermarkAssetsTab;
